import { Injectable, ApplicationRef, Injector, EventEmitter } from "@angular/core";
import { MessagerService } from "@farris/ui-messager";
import { ContextService } from "../services/context.service";
import { EditorEventType } from "../event-bus/config";
import { IEventBus } from '../event-bus/lib/types';
import { EventBusHandler } from "../event-bus/handler";
import { TabsHandler } from "../interface/handler/tabs-handler";
import { EditorHandler } from "../interface/handler/editor-handler";
import { RootHandler } from "../interface/handler/root-handler";
import { Tab } from "../interface/tab";
import { FileItem } from "../interface/file-item";
import { NavTreeService } from '../services/nav-tree.service';
import { NavTreeData } from "../interface/tree";
import { throttle } from 'lodash-es';
import { NotifyService, NotifyOptions } from "@farris/ui-notify";
import { from, fromEvent, Observable, Subject } from "rxjs";
import { CommonEvent } from "../interface/event";
import { SaveResult } from "../event-bus/lib/types";
import { IEditorController } from "./types";
import { concatMap } from "rxjs/operators";
import { LocaleService } from '@farris/ui-locale';
import { CODE_VIEW_DESIGNER_LOCALE } from '../utils/locale'
let DEFAULT_SAVE_ALL_SUCCESS_TIP = "全部保存成功";
let DEFAULT_SAVE_SUCCESS_TIP = "保存成功";
let DEFAULT_SAVE_FAILED_TIP = "保存失败";

/**
 * 代码编辑器中央控制器
 * @remarks
 * 掌握标签页、中心编辑器等组件实例，对外封装打开文件、关闭文件等基本接口
 */
@Injectable()
export class EditorController implements IEditorController {

  private root: RootHandler;

  /** 标签页组件实例 */
  private tabs: TabsHandler;

  /** 中心编辑器组件实例 */
  private editor: EditorHandler;

  get eventBus(): IEventBus {
    return this.eventBusHandler && this.eventBusHandler.eventBus;
  }

  /** 保存当前页签的文件 - 节流版本 */
  public throttledSaveCurrentFile: () => void;

  /** 全局按键事件 */
  keydownEmitter = new Subject<KeyboardEvent>();

  /**
   * 通知表单设计器代码编辑器内容已修改
   */
  private detectFileDirty:EventEmitter<any> = null

  //多语资源
  localeData = CODE_VIEW_DESIGNER_LOCALE["zh-CHS"].codeViewDesigner

  constructor(
    private injector: Injector,  // 防止依赖变更导致需要重新打包
    private applicationRef: ApplicationRef,
    private context: ContextService,
    private eventBusHandler: EventBusHandler,
    private navTreeService: NavTreeService,
    private msgSrv: MessagerService,
    private notifySrv: NotifyService
  ) {

    const localeService = this.injector.get(LocaleService)
    if(localeService) {
      this.localeData = localeService.getValue('codeViewDesigner') 
    }
    DEFAULT_SAVE_ALL_SUCCESS_TIP =  this.localeData.saveSuccessAll || "全部保存成功";
    DEFAULT_SAVE_SUCCESS_TIP = this.localeData.saveSuccess || "保存成功";
    DEFAULT_SAVE_FAILED_TIP = this.localeData.saveFailed || "保存失败";
    this.eventBusHandler.getInstance();
    this.throttledSaveCurrentFile = throttle(() => {
      this.doSaveCurrentFile(true);
    }, 600);
  }

  public getContextService(): ContextService {
    return this.context;
  }

  /**
   * 初始化中央控制器
   * @param root 代码编辑器根组件
   * @param tabs 标签页组件
   * @param editor 中心编辑器组件
   */
  public init(root: RootHandler, tabs: TabsHandler, editor: EditorHandler, detectFileDirty?: EventEmitter<any>): void {
    // 设置各个子组件实例
    this.tabs = tabs;
    this.editor = editor;
    this.root = root;
    if(detectFileDirty) {
      this.detectFileDirty = detectFileDirty
    }
    // 订阅内部和外部事件
    this.subscribeInnerEvent();
    this.subscribeOuterEvent();
    this.listenShortcut();
    // 执行组件的全局初始化操作
    this.tabs.globalInit();
  }

  /** 监听快捷键 - 仅初始化时调用一次 */
  private listenShortcut(): void {
    fromEvent(document, 'keydown').subscribe((event: KeyboardEvent) => {
      this.keydownEmitter.next(event);
    });
    this.keydownEmitter.subscribe((event: KeyboardEvent) => {
      if (!this.root.onTop || event.repeat || !event.key) {
        return;
      }
      // 监听保存快捷键 ctrl+s
      if (event.key.toLowerCase() === "s" && event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.throttledSaveCurrentFile();
        return;
      }
      // 监听侧边栏收折快捷键 ctrl+b
      if (event.key.toLowerCase() === "b" && event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.root.toggleNavPanel();
        return;
      }
    });
  }

  private subscribeOuterEvent(): void {
    // 监听导航树打开文件事件
    this.eventBus.subscribe(EditorEventType.OpenFile, (info: { path: string, data: NavTreeData }) => {
      this.openFile(info.path, info.data.appendInfo);
      this.root.detectChanges();
    }, this);
    // 监听文件内容变更事件
    this.eventBus.subscribe(EditorEventType.FileChanged, (info: { path: string, changed: boolean }) => {
      const fileItem = this.context.getFileItemByPath(info.path);
      const tab = this.tabs.getTab(info.path);
      if (!fileItem || !tab) {
        return;
      }
      fileItem.isCodeFileDirty = !!info.changed;
      tab.isDirty = fileItem.isDirty;
      this.notifyFormDirtyInfo()
      this.root.detectChanges();
    }, this);
    // 监听设计器文件内容变更事件
    this.eventBus.subscribe(EditorEventType.DesignerFileChanged, (info: { path: string, changed: boolean }) => {
      const fileItem = this.context.getFileItemByPath(info.path);
      const tab = this.tabs.getTab(info.path);
      if (!fileItem || !tab) {
        return;
      }
      fileItem.isDesignerFileDirty = !!info.changed;
      tab.isDirty = fileItem.isDirty;
      //通知表单，代码编辑器已修改
      this.notifyFormDirtyInfo()
      this.root.detectChanges();
    }, this);
    // 监听代发消息提示事件
    this.eventBus.subscribe(EditorEventType.NotifyMessage, (option: NotifyOptions) => {
      if (!!option && !!option.type) {
        const type = option.type;
        this.notifySrv[type](option);
        this.root.detectChanges();
      }
    }, this);
  }

  private subscribeInnerEvent(): void {
    // 监听标签页关闭事件
    this.tabs.beforeClose.subscribe((tab: Tab) => {
      this.closeFile(tab.id);
      this.root.detectChanges();
    });
    // 监听标签页选中事件
    this.tabs.selected.subscribe((tab: Tab) => {
      this.handleTabSelected(tab.id);
      this.root.detectChanges();
    });
  }

  public openFile(path: string, appendInfo?: any): void {
    // 判断文件是否已经被打开
    const isFileExist = this.context.isFileExist(path);
    if (isFileExist) {
      // 如果文件已经被打开，则直接显示已有文件
      const oldFileItem = this.context.getFileItemByPath(path);
      const mainPath = oldFileItem && oldFileItem.mainPath;
      if (!!mainPath && !!appendInfo) {
        this.eventBus.setAppendInfo(mainPath, appendInfo);
      }
      this.showFile(path);
      return;
    }
    // 文件没有被打开，新增页签并切换编辑器
    const newFileItem = this.context.openFile(path);
    if (!newFileItem) {
      return;  // 传入的path是可能为空的
    }
    const mainPath = newFileItem.mainPath;
    if (!!mainPath && !!appendInfo) {
      this.eventBus.setAppendInfo(mainPath, appendInfo);
    }
    this.tabs.addTab(Tab.getNewTabFromFileItem(newFileItem), true);
    const success = this.editor.open(newFileItem.mainPath, { "open": path });
    if (!success) {
      console.error("不存在针对该文件的打开方式配置", newFileItem.mainPath);
      return;
    }
    this.showFile(path);  // 为了更新关联文件标题以及切换关联文件内容
  }

  public showFile(path: string): void {
    // 获取当前已有文件条目
    const fileItem = this.context.getFileItemByPath(path);
    // 如果正在显示这个页签，则仅需要进行关联文件切换
    const isCurrentTab = this.context.currentFilePath === path;
    // 切换当前显示文件路径
    this.context.currentFilePath = path;
    // 如果路径切换不成功，说明文件条目不存在
    if (this.context.currentFilePath !== path) {
      return;
    }
    const mainPath = fileItem.mainPath;
    fileItem.curPath = path;
    // 按照新的路径更新标签页的标题和图标
    this.updateTabTitle(path);
    // 按照新的路径进行关联文件切换
    this.eventBus.emit(EditorEventType.SwitchRelevantFile, path, mainPath);
    // 显示目标页签和标签页
    if (!isCurrentTab) {
      this.tabs.selectById(mainPath);
      this.editor.show(mainPath);
    }
  }

  /**
   * 更新标签页的标题和图标
   * @param path 文件路径
   */
  private updateTabTitle(path: string): void {
    const fileItem = this.context.getFileItemByPath(path);
    if (!fileItem) {
      return;
    }
    const tab = this.tabs.getTab(fileItem.mainPath);
    tab.updateTitle(
      FileItem.getTitleFromPath(path),
      FileItem.getIconFromPath(path)
    );
  }

  public closeFile(path: string, force?: boolean): void {
    const fileItem = this.context.getFileItemByPath(path);
    if (!fileItem) {
      return;
    }
    if (!fileItem.isDirty || force) {
      this.directCloseFile(fileItem);
      return;
    }
    this.closeFiles([fileItem]).subscribe(() => {
      this.notifyFormDirtyInfo()
     });
  }

  public closeAllSavedFile(): void {
    const items = this.context.fileItems;
    const itemsToRemove = [];
    for (const item of items) {
      if (!item.isDirty) {
        itemsToRemove.push(item);
      }
    }
    for (const item of itemsToRemove) {
      this.directCloseFile(item);
    }
  }

  public closeToTheRight(path: string): void {
    const itemsToRemove = [];
    const tabs = this.tabs.tabs;
    let beginToRemove = false;
    for (const tab of tabs) {
      // 先找到指定的标签页
      if (!beginToRemove) {
        if (tab.id === path) {
          beginToRemove = true;
        }
        continue;
      }
      // 移除该标签页右侧的所有条目
      const item = this.context.getFileItemByPath(tab.id);
      item && itemsToRemove.push(item);
    }
    this.closeFiles(itemsToRemove).subscribe(() => {
      this.notifyFormDirtyInfo()
      this.showFile(path);  // 防止停留在其它保存失败的页面上
    });
  }

  public closeAllFile(): void {
    const items = this.context.fileItems;
    const itemsToRemove = [];
    for (const item of items) {
      itemsToRemove.push(item);
    }
    this.closeFiles(itemsToRemove).subscribe(() => { 
      this.notifyFormDirtyInfo()
    });
  }

  public closeOtherFile(path: string): void {
    const items = this.context.fileItems;
    const itemsToRemove = [];
    for (const item of items) {
      if (item.mainPath !== path) {
        itemsToRemove.push(item);
      }
    }
    this.closeFiles(itemsToRemove).subscribe(() => {
      this.notifyFormDirtyInfo()
      this.showFile(path);  // 防止停留在其它保存失败的页面上
    });
  }

  /**
   * 关闭指定的几个文件条目（对应的标签页）
   * @remarks 如果存在未保存的文件则先显示该文件并弹框提示用户是否保存
   * @param items 文件条目
   * @returns 返回Observable以便在执行完成后追加操作，别忘记订阅
   */
  private closeFiles(items: FileItem[]): Observable<void> {
    return from(items).pipe(
      concatMap((item: FileItem) => {
        return new Promise((resolve) => {
          // 如果文件未修改则直接关闭
          if (!item.isDirty) {
            this.directCloseFile(item);
            resolve();
          } else {  // 如果存在未保存的修改，则提示用户成功保存后关闭
            this.showFile(item.curPath);  // 询问用户是否保存前先切换到对应的页面
            // 优先显示标签页中的标题
            const tab = this.tabs && this.tabs.getTab(item.mainPath);
            const title = tab && tab.title || item.title;
            const warningText = this.localeData.saveVailate ? this.localeData.saveVailate.replace('{param}', title) : `是否要保存对${title}的更改？`;
            const message = this.msgSrv.show('warning', warningText, {
              initialState: {
                buttons: [
                  {
                    text: this.localeData.save || '保存',
                    cls: 'btn btn-primary',
                    handle: () => {
                      this.saveAndCloseFile(item.mainPath, true);
                      message.close();
                      resolve();
                    }
                  },
                  {
                    text: this.localeData.unsave || '不保存',
                    cls: 'btn btn-secondary',
                    handle: () => {
                      this.directCloseFile(item);
                      message.close();
                      this.root.closeWithoutSave.emit();
                      resolve();
                    }
                  },
                  {
                    text: this.localeData.cancel || '取消',
                    cls: 'btn btn-secondary',
                    handle: () => {
                      message.close();
                      resolve();
                    }
                  }
                ]
              }
            }, this.localeData.unsaveInfo || '如果不保存，你的更改将丢失。');
          }
        });
      })
    );
  }

  /**
   * 保存并关闭文件
   * @param path 标签页路径
   * @param ignoreSuccessTip 不弹出保存成功提示
   */
  private async saveAndCloseFile(path: string, ignoreSuccessTip: boolean = false): Promise<void> {
    const result = await this.doSaveCurrentFile(true, path, ignoreSuccessTip);
    if (result && result.success) {
      this.closeFile(path, true);
    }
  }

  /** 直接关闭文件 */
  private directCloseFile(item: FileItem) {
    if (this.context.removeFile(item.mainPath)) {
      const token = item.mainPath;
      this.eventBus.emit(EditorEventType.CloseFile, token, token);
      this.tabs.removeTab(token);
      this.editor.close(token);
      this.eventBus.clearNotificationQueue(token);
      this.eventBus.clearSaveCallback(token);
      // 当文件关闭时，需要将当前文件路径也设置为空
      if (this.context.currentFileItem && this.context.currentFileItem.mainPath === token) {
        this.context.currentFilePath = null;
      }
    }
  }

  /**
   * 保存当前文件
   * @param path 指定一个标签页路径，保存该标签页的文件（而不是保存当前文件）
   * @remarks 无论当前文件是否有变更都重新执行其保存回调函数
   * @returns 保存结果，如果为空则表示当前没有文件被打开
   */
  private async saveCurrentFile(path?: string): Promise<SaveResult> {
    // 获取当前文件条目路径
    const fileItem = path ? this.context.getFileItemByPath(path) : this.context.currentFileItem;
    if (!fileItem) {
      return null;
    }
    const mainPath = fileItem.mainPath;
    // 获取保存回调函数
    const callbackInfo = this.eventBus.getSaveCallback(mainPath);
    const callback = callbackInfo && callbackInfo.callback;
    if (!callback || typeof callback !== 'function') {
      return null;
    }
    // 执行保存操作
    let saveResult: SaveResult = null;
    try {
      saveResult = await callback();
    } catch {
      saveResult = { success: false };
    }
    this.root.saved.emit();
    saveResult && (saveResult.curPath = fileItem.curPath);
    saveResult && (saveResult.mainPath = fileItem.mainPath);
    saveResult && (saveResult.name = fileItem.title);
    return saveResult;
  }

  /**
   * 保存全部文件
   * @remarks 将保存全部有变更的文件（标签页可能配置“未变更也保存”选项）
   * @returns 保存结果
   */
  private async saveAllFile(): Promise<SaveResult[]> {
    // 为保存结果设置默认的路径和名称信息
    const setDefaultPath = (fileItem: FileItem, saveResult: SaveResult): SaveResult => {
      saveResult && (saveResult.curPath = fileItem.curPath);
      saveResult && (saveResult.mainPath = fileItem.mainPath);
      saveResult && (saveResult.name = fileItem.title);
      return saveResult;
    }
    const results: Promise<SaveResult>[] = [];
    for (const fileItem of this.context.fileItems) {
      const mainPath = fileItem.mainPath;
      const callbackInfo = this.eventBus.getSaveCallback(mainPath);
      const callback = callbackInfo && callbackInfo.callback;
      if (!callback || typeof callback !== 'function') {
        continue;
      }
      // 如果文件数据未变更且未设置“未变更也保存”选项，则不保存该条目
      const alwaysSave = callbackInfo.alwaysSave;
      if (!fileItem.isDirty && !alwaysSave) {
        continue;
      }
      const saveResult = callback().then((result) => {
        return setDefaultPath(fileItem, result);
      }).catch(() => {
        return setDefaultPath(fileItem, { success: false });
      });
      results.push(saveResult);
    }
    let saveResultArr: SaveResult[] = [];
    try {
      saveResultArr = await Promise.all(results);
    } catch (err) {
      console.error(err);
    }
    // this.root.saved.emit();
    return saveResultArr;
  }

  public async doSaveCurrentFile(notifyResult: boolean = false, path?: string, ignoreSuccessTip: boolean = false): Promise<SaveResult> {
    const result = await this.saveCurrentFile(path);
    if (result && !!result.mainPath && !!result.success) {
      this.resetFileChanged(result.mainPath);
    }
    if (notifyResult && !!result) {
      if (!result.success || !ignoreSuccessTip) {  // 判断是否忽略成功提示信息
        this.notifyOneSaveResult(result);
      }
    }
    return result;
  }

  private notifyOneSaveResult(result: SaveResult): void {
    if (!result) {
      return;
    }
    if (result.success) {
      const tip = result.tip || DEFAULT_SAVE_SUCCESS_TIP;
      this.notifySrv.success(tip);
    } else {
      const tip = result.tip || DEFAULT_SAVE_FAILED_TIP;
      this.notifySrv.error({ type: 'error', msg: tip, timeout: 3500 });
    }
  }

  public async doSaveAllFile(notifyResult: boolean = false, ignoreSuccessTip: boolean = false): Promise<SaveResult[]> {
    const resultArr = await this.saveAllFile();
    for (const result of resultArr) {
      if (result && !!result.mainPath && !!result.success) {
        this.resetFileChanged(result.mainPath);
      }
    }
    if (notifyResult && resultArr && resultArr.length > 0) {
      this.notifyAllSaveResult(resultArr, ignoreSuccessTip);
    }
    return resultArr;
  }

  private notifyAllSaveResult(results: SaveResult[], ignoreSuccessTip: boolean = false): void {
    if (!results || results.length === 0) {
      return;
    }
    let allSuccess = true;
    for (const result of results) {
      if (!result.success) {
        const tip = result.tip || DEFAULT_SAVE_FAILED_TIP;
        const message = `${result.name} ${tip}`;
        const modalOptions = {
          title: this.localeData.errorInfo || '错误提示',
          showMaxButton: false,
          safeHtml: false
        };
        this.msgSrv.show('error', message, modalOptions);
        allSuccess = false;
      }
    }
    if (allSuccess && !ignoreSuccessTip) {
      this.notifySrv.success(DEFAULT_SAVE_ALL_SUCCESS_TIP);
    }
  }

  /**
   * 是否全部保存成功
   * @param results 保存结果数组
   * @param 是否全部成功
   */
  private isAllSavedSuccess(results: SaveResult[]): boolean {
    if (!results || results.length === 0) {
      return true;
    }
    for (const result of results) {
      if (!result.success) {
        return false;
      }
    }
    return true;
  }

  private resetFileChanged(mainPath: string): void {
    const fileItem = this.context.getFileItemByPath(mainPath);
    const tab = this.tabs.getTab(mainPath);
    if (!fileItem || !tab) {
      return;
    }
    fileItem.isCodeFileDirty = false;
    fileItem.isDesignerFileDirty = false;
    tab.isDirty = fileItem.isDirty;
    this.root.detectChanges();
  }

  /**
   * 处理标签页选中事件
   * @param path 文件路径
   */
  private handleTabSelected(path: string): void {
    const fileItem = this.context.getFileItemByPath(path);
    // 如果路径与上下文中记录的当前文件路径不符，则需要切换相关组件的内容
    if (path !== this.context.currentFilePath && !!fileItem) {
      this.showFile(fileItem.curPath);
    }
    // 通知左侧导航树切换选中节点（注意：该事件必须在当前文件路径变更之后发出）
    this.eventBus.emit(EditorEventType.TabSelected, fileItem.curPath);
  }

  public refreshNavTree(selectNodePath?: string, type?: string[]): void {
    this.navTreeService.refresh.emit({
      type, selectNodePath
    });
  }

  public switchView(force?: boolean): void {
    if (!!force) {
      this.directSwitchView();
      return;
    }
    const hasDirtyFile = this.context.hasDirtyFile();
    if (hasDirtyFile) {
      const message = this.msgSrv.show('warning', this.localeData.unsaveInfo2 || '存在尚未保存的文件，是否继续跳转？', {
        initialState: {
          buttons: [
            {
              text: this.localeData.saveAndJump || '保存并跳转',
              cls: 'btn btn-primary',
              handle: async () => {
                message.close();
                try {
                  const results = await this.doSaveAllFile(true, true);
                  this.root.saved.emit()
                  if (this.isAllSavedSuccess(results)) {
                    this.directSwitchView();
                  }
                } catch (err) { }
              }
            },
            {
              text: this.localeData.nosaveAndJump || '仅跳转',
              cls: 'btn btn-secondary',
              handle: () => {
                message.close();
                this.root.closeWithoutSave.emit();
                this.directSwitchView();
              }
            },
            {
              text: this.localeData.cancel || '取消',
              cls: 'btn btn-secondary',
              handle: () => {
                message.close();
              }
            }
          ]
        }
      });
    } else {
      this.directSwitchView();
    }
  }

  private directSwitchView(): void {
    this.root.switchView.emit();
  }

  public setGlobalContextParam(key: string, value: any): any {
    return this.eventBus.setGlobalContextParam(key, value);
  }

  public getGlobalContextParam(key: string): any {
    return this.eventBus.getGlobalContextParam(key);
  }

  public detectChangesFromRoot(): void {
    this.applicationRef.tick();
  }

  public sendNotification(path: string, event: CommonEvent): Observable<any> | null {
    // 如果找不到path对应的文件条目，说明文件尚未打开，静默失败
    const fileItem = this.context.getFileItemByPath(path);
    if (!fileItem || !fileItem.mainPath) {
      return null;
    }
    return this.eventBus.pushNotificationQueue(fileItem.mainPath, event);
  }

  /**
   * 直接关闭所有标签页
   */
  private closeAllFileDirectly(): void {
    const items = this.context.fileItems;
    const itemsToRemove = [];
    for (const item of items) {
      itemsToRemove.push(item);
    }
    for (const itemToRemove of itemsToRemove) {
      try {
        this.directCloseFile(itemToRemove);
      } catch (err) {
        console.log("销毁标签页失败", err);
      }
    }
  }

  /** 销毁代码视图，释放资源 */
  public destroy(): void {
    this.closeAllFileDirectly();
    this.eventBusHandler.destroy();
  }

  /**
   * 
   */
  private notifyFormDirtyInfo() {
    if(this.detectFileDirty) {
      let flag = this.context.hasDirtyFile()
      this.detectFileDirty.emit(flag)
    }
  }
}
